// The create-project form, shared by the welcome and the "New project"
// dialog: name, key, description and an optional GitHub repository. The
// hook owns the state and the mutation so each caller can place its own
// buttons (the welcome's sit in a step footer, the dialog's below the form).

import { forwardRef, useEffect, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { PROJECT_KEY_RE, deriveProjectKey, type Project } from '@goblic/shared'
import { ApiError, githubApi, projectsApi } from '../../lib/api-client'
import { Field, Notice } from '../ui'

/** Roles the API lets create a project (POST /projects needs project.create). */
export const PROJECT_MANAGER_ROLES = ['workspace_owner', 'administrator']

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback
}

const EMPTY_FORM = { name: '', key: '', repoSlug: '', branch: 'main', description: '' }

export function useCreateProjectForm({
  workspaceId,
  projects,
  githubConnected,
  onCreated,
}: {
  workspaceId: string
  projects: Project[]
  githubConnected: boolean
  onCreated: (project: Project) => void
}) {
  const [form, setForm] = useState(EMPTY_FORM)

  const reposQuery = useQuery({
    queryKey: ['github-repos', workspaceId],
    queryFn: () => githubApi.repos(workspaceId),
    enabled: githubConnected,
  })
  const availableRepos = reposQuery.data?.repos ?? []

  const knownRepo = availableRepos.some((r) => r.fullName === form.repoSlug.trim())
  const branchesQuery = useQuery({
    queryKey: ['github-branches', workspaceId, form.repoSlug],
    queryFn: () => githubApi.branches(workspaceId, form.repoSlug.trim()),
    enabled: githubConnected && knownRepo,
  })
  const availableBranches = branchesQuery.data?.branches ?? []

  // The key the server would derive, shown until someone types their own.
  const suggestedKey = form.name.trim()
    ? deriveProjectKey(form.name, projects.map((p) => p.key))
    : ''
  const key = (form.key.trim() || suggestedKey).toUpperCase()
  const keyValid = !form.key.trim() || PROJECT_KEY_RE.test(key)

  const create = useMutation({
    mutationFn: async () => {
      const project = await projectsApi.create({
        workspaceId,
        name: form.name.trim(),
        description: form.description.trim(),
        ...(form.key.trim() ? { key } : {}),
      })
      const slug = form.repoSlug.trim().replace(/\/$/, '')
      if (slug) {
        await projectsApi.addRepository(project.id, workspaceId, {
          url: `https://github.com/${slug}`,
          defaultBranch: form.branch.trim() || 'main',
          isDefault: true,
        })
      }
      return project
    },
    onSuccess: (project) => {
      setForm(EMPTY_FORM)
      onCreated(project)
    },
  })

  function submit(event: FormEvent): void {
    event.preventDefault()
    if (keyValid) create.mutate()
  }

  function onRepoChange(slug: string): void {
    const repo = slug.split('/').pop()
    setForm((f) => ({
      ...f,
      repoSlug: slug,
      name: f.name || !repo ? f.name : repo,
    }))
  }

  return {
    form,
    setForm,
    suggestedKey,
    keyValid,
    availableRepos,
    availableBranches,
    create,
    submit,
    onRepoChange,
    pending: create.isPending,
    canSubmit: !create.isPending && form.name.trim() !== '' && keyValid,
  }
}

export type CreateProjectController = ReturnType<typeof useCreateProjectForm>

export function CreateProjectForm({
  controller: c,
  formId,
  idPrefix,
}: {
  controller: CreateProjectController
  formId: string
  idPrefix: string
}) {
  const { form, setForm } = c
  return (
    <form id={formId} className="space-y-4" onSubmit={c.submit}>
      <div className="grid gap-4 sm:grid-cols-[1fr_9rem]">
        <Field label="Project name" htmlFor={`${idPrefix}-project-name`}>
          <input
            id={`${idPrefix}-project-name`}
            data-autofocus
            className="field-input"
            placeholder="My app"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
            autoFocus
          />
        </Field>
        <Field
          label="Key"
          htmlFor={`${idPrefix}-project-key`}
          hint={
            c.keyValid ? undefined : (
              <span className="text-danger">A letter, then 1–9 letters or digits.</span>
            )
          }
        >
          <input
            id={`${idPrefix}-project-key`}
            className="field-input font-mono uppercase"
            placeholder={c.suggestedKey || 'GOB'}
            value={form.key}
            maxLength={10}
            onChange={(event) => setForm({ ...form, key: event.target.value.toUpperCase() })}
            autoCapitalize="characters"
            spellCheck={false}
          />
        </Field>
      </div>
      <Field
        label="What is it? (optional)"
        htmlFor={`${idPrefix}-project-description`}
        hint="One or two sentences the goblins read before every task."
      >
        <textarea
          id={`${idPrefix}-project-description`}
          className="field-input"
          rows={2}
          value={form.description}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
        />
      </Field>
      <div className="border-t border-line pt-4">
        <p className="mb-3 text-sm font-medium text-ink-2">Repository (optional)</p>
        <div className="space-y-4">
          <Field
            label="Repository"
            htmlFor={`${idPrefix}-repo`}
            hint="owner/repo — e.g. acme/webshop"
          >
            <RepoCombobox
              value={form.repoSlug}
              onChange={c.onRepoChange}
              repos={c.availableRepos}
            />
          </Field>
          {form.repoSlug.trim() ? (
            <Field label="Main branch" htmlFor={`${idPrefix}-branch`}>
              <BranchCombobox
                value={form.branch}
                onChange={(branch) => setForm({ ...form, branch })}
                branches={c.availableBranches}
              />
            </Field>
          ) : null}
        </div>
      </div>
      {c.create.isError ? (
        <Notice tone="error">
          {errorText(c.create.error, "Couldn't create the project.")}
        </Notice>
      ) : null}
    </form>
  )
}

const LIST_MAX_HEIGHT = 224

/**
 * The dropdown list of a combobox, portalled to <body> and positioned against
 * the input: inside a dialog's scroll area an absolutely positioned list is
 * clipped by the dialog. Opens upwards when there is no room below.
 */
const FloatingList = forwardRef<HTMLUListElement, { anchorRef: RefObject<HTMLElement | null>; children: ReactNode }>(
  function FloatingList({ anchorRef, children }, ref) {
    const [box, setBox] = useState<{ left: number; width: number; top?: number; bottom?: number; maxHeight: number } | null>(null)

    useLayoutEffect(() => {
      function place() {
        const el = anchorRef.current
        if (!el) return
        const r = el.getBoundingClientRect()
        const below = window.innerHeight - r.bottom - 8
        const above = r.top - 8
        const up = below < Math.min(LIST_MAX_HEIGHT, 140) && above > below
        setBox(
          up
            ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(LIST_MAX_HEIGHT, above) }
            : { left: r.left, width: r.width, top: r.bottom + 4, maxHeight: Math.min(LIST_MAX_HEIGHT, below) },
        )
      }
      place()
      window.addEventListener('resize', place)
      window.addEventListener('scroll', place, true)
      return () => {
        window.removeEventListener('resize', place)
        window.removeEventListener('scroll', place, true)
      }
    }, [anchorRef])

    if (!box) return null
    return createPortal(
      <ul
        ref={ref}
        role="listbox"
        style={{ position: 'fixed', left: box.left, width: box.width, top: box.top, bottom: box.bottom, maxHeight: box.maxHeight }}
        className="z-50 overflow-y-auto rounded-control border border-line bg-surface py-1 shadow-overlay"
      >
        {children}
      </ul>,
      document.body,
    )
  },
)

export function RepoCombobox({
  value,
  onChange,
  repos,
}: {
  value: string
  onChange: (value: string) => void
  repos: { fullName: string }[]
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(value)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    if (!open) setQuery(value)
  }, [value, open])

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      const target = e.target as Node
      if (containerRef.current && !containerRef.current.contains(target) && !listRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const filtered = repos
    .filter((r) => r.fullName.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 80)

  function commit(slug: string) {
    onChange(slug)
    setQuery(slug)
    setOpen(false)
    setActiveIndex(-1)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value
    setQuery(v)
    onChange(v)
    setActiveIndex(-1)
    setOpen(true)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, -1))
    } else if (e.key === 'Enter' && activeIndex >= 0 && filtered[activeIndex]) {
      e.preventDefault()
      commit(filtered[activeIndex]!.fullName)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      listRef.current.children[activeIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          className="field-input font-mono pr-9"
          placeholder={repos.length > 0 ? "Select or type owner/repo" : "acme/webshop"}
          value={query}
          onChange={handleChange}
          onFocus={() => repos.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          autoCapitalize="none"
          spellCheck={false}
        />
        {repos.length > 0 ? (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o) }}
            className="absolute inset-y-0 right-0 flex items-center px-2.5 text-ink-3 hover:text-ink"
            aria-label="Toggle repo list"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}
      </div>

      {open && filtered.length > 0 ? (
        <FloatingList ref={listRef} anchorRef={containerRef}>
          {filtered.map((repo, i) => (
            <li
              key={repo.fullName}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={(e) => { e.preventDefault(); commit(repo.fullName) }}
              onMouseEnter={() => setActiveIndex(i)}
              className={`cursor-pointer px-3 py-1.5 font-mono text-sm ${
                i === activeIndex ? 'bg-action text-on-action' : 'text-ink hover:bg-sunken'
              }`}
            >
              {repo.fullName}
            </li>
          ))}
        </FloatingList>
      ) : null}
    </div>
  )
}

export function BranchCombobox({
  value,
  onChange,
  branches,
}: {
  value: string
  onChange: (value: string) => void
  branches: { name: string }[]
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(value)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    if (!open) setQuery(value)
  }, [value, open])

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      const target = e.target as Node
      if (containerRef.current && !containerRef.current.contains(target) && !listRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const filtered = branches
    .filter((b) => b.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 50)

  function commit(name: string) {
    onChange(name)
    setQuery(name)
    setOpen(false)
    setActiveIndex(-1)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value
    setQuery(v)
    onChange(v)
    setActiveIndex(-1)
    setOpen(true)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, -1))
    } else if (e.key === 'Enter' && activeIndex >= 0 && filtered[activeIndex]) {
      e.preventDefault()
      commit(filtered[activeIndex]!.name)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      listRef.current.children[activeIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          className="field-input font-mono pr-9"
          placeholder="main"
          value={query}
          onChange={handleChange}
          onFocus={() => branches.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          autoCapitalize="none"
          spellCheck={false}
        />
        {branches.length > 0 ? (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o) }}
            className="absolute inset-y-0 right-0 flex items-center px-2.5 text-ink-3 hover:text-ink"
            aria-label="Toggle branch list"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}
      </div>

      {open && filtered.length > 0 ? (
        <FloatingList ref={listRef} anchorRef={containerRef}>
          {filtered.map((branch, i) => (
            <li
              key={branch.name}
              role="option"
              aria-selected={i === activeIndex}
              onMouseDown={(e) => { e.preventDefault(); commit(branch.name) }}
              onMouseEnter={() => setActiveIndex(i)}
              className={`cursor-pointer px-3 py-1.5 font-mono text-sm ${
                i === activeIndex ? 'bg-action text-on-action' : 'text-ink hover:bg-sunken'
              }`}
            >
              {branch.name}
            </li>
          ))}
        </FloatingList>
      ) : null}
    </div>
  )
}
